import React, {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import useEventTrackingLogEvent, { categories, eventSubTypes, eventTypes } from 'EventTracking/useEventTrackingLogEvent';

// Context holds selected path state and derived resources used by header/content/levers
const RoadmapExplorerPathsContext = createContext(null);

const getDefaultPath = (roadmapExplorerResource) => roadmapExplorerResource
  ?.attr('paths')
  ?.resources()
  ?.head();

const buildPathsByID = (roadmapExplorerResource) => Object.fromEntries(
  (roadmapExplorerResource?.attr('paths')?.resources()?.list() ?? [])
    .map((path) => ([path.id(), path])),
);

const equalArrays = (arr1, arr2) => {
  if (!arr1 && !arr2) {
    return true;
  }
  if (!arr1 || !arr2) { // Due to the above if, this only happens if exactly 1 of the arrays is truthy and the other is falsey
    return false;
  }
  return arr1.length === arr2.length && arr1.every((arr1Val, i) => arr1Val === arr2[i]);
};

export const RoadmapExplorerPathsProvider = ({ resource, children }) => {
  let roadmapExplorerResource = resource;
  const [showGoals, setShowGoals] = useState(false);
  const [newlyCreatedPathID, setNewlyCreatedPathID] = useState();
  const pathsByID = useMemo(() => buildPathsByID(roadmapExplorerResource), [roadmapExplorerResource]);
  const [_comparisonPaths, setComparisonPaths] = useState(null);
  const [_selectedPath, setSelectedPathState] = useState(getDefaultPath(roadmapExplorerResource)?.id());
  const [_chartResource, setChartResource] = useState(roadmapExplorerResource?.attr('chart')?.resources()?.head?.() ?? null);
  const [goalsByRoadmap, setGoalsByRoadmap] = useState(roadmapExplorerResource?.attr('goalsByRoadmap'));
  const { logStandardizedEvent } = useEventTrackingLogEvent({
    nodeName: 'Roadmap Explorer chart',
  });

  useEffect(() => {
    setChartResource(roadmapExplorerResource?.attr('chart')?.resources()?.head?.() ?? null);
  }, [roadmapExplorerResource]);

  let selectedPath = _selectedPath;
  let comparisonPaths = _comparisonPaths;

  // If the selected path is gone, deal with it
  if (selectedPath !== undefined && !pathsByID[selectedPath]) {
    selectedPath = getDefaultPath(roadmapExplorerResource)?.id();
  }
  // If a comparison path is gone, deal with it
  if (comparisonPaths && comparisonPaths.length > 0 && comparisonPaths.some(id => !pathsByID[id])) {
    comparisonPaths = comparisonPaths.filter(id => pathsByID[id]);
    if (comparisonPaths.length === 0) {
      comparisonPaths = null;
    }
  }
  // If someone just created a new path and we just got it, use that new path instead and leave comparison mode
  if (newlyCreatedPathID && pathsByID[newlyCreatedPathID]) {
    selectedPath = newlyCreatedPathID;
    comparisonPaths = null;
  }

  // ========== IMPORTANT: move state sync out of render phase ==========
  React.useEffect(() => {
    // sync selectedPath if it changed due to missing/created paths
    if (_selectedPath !== selectedPath) {
      setSelectedPathState(selectedPath);
    }
  }, [_selectedPath, selectedPath]);

  React.useEffect(() => {
    // sync comparisonPaths after pruning/clearing
    if (!equalArrays(_comparisonPaths, comparisonPaths)) {
      logStandardizedEvent(categories.roadmapExplorer, eventTypes.compare, comparisonPaths && comparisonPaths.length > 1 ? eventSubTypes.select : eventSubTypes.stop);
      setComparisonPaths(comparisonPaths);
    }
  }, [_comparisonPaths, comparisonPaths]);

  React.useEffect(() => {
    // clear the "new path" flag after it's been applied
    if (newlyCreatedPathID && pathsByID[newlyCreatedPathID]) {
      setNewlyCreatedPathID(undefined);
    }
  }, [newlyCreatedPathID, pathsByID]);
  // ================================================================

  const setSelectedPath = React.useCallback((pathId) => {
    setComparisonPaths((prev) => {
      if (prev != null && !prev.includes(pathId)) {
        return [...prev, pathId];
      }
      return prev;
    });
    setSelectedPathState(pathId);
  }, []);

  const exitComparisonMode = React.useCallback(() => setComparisonPaths(null), []);

  const refreshSelectedPathDataFromLevers = React.useCallback(async () => {
    [roadmapExplorerResource] = await roadmapExplorerResource.link('self').get();
    setChartResource(roadmapExplorerResource.attr('chart').resources()?.head?.() ?? null);
    setGoalsByRoadmap(roadmapExplorerResource.attr('goalsByRoadmap'));
  }, [roadmapExplorerResource]);

  const pathsList = useMemo(() => {
    return roadmapExplorerResource?.attr('paths')?.resources()?.list?.() ?? [];
  }, [roadmapExplorerResource]);

  const pathResource = pathsByID[selectedPath];

  const goalsOnCurrentRoadmap = useMemo(() => {
    return (
      goalsByRoadmap
        ?.resources()
        ?.list?.()
        ?.find((res) => res.id() === selectedPath)
        ?.attr('goals')
        ?.resources()
        ?.list?.() ?? []
    );
  }, [goalsByRoadmap, selectedPath]);

  const value = React.useMemo(() => ({
    // data
    resource: roadmapExplorerResource,
    pathsList,
    selectedPath,
    pathResource,
    comparisonPaths,
    chartResource: _chartResource,
    goalsOnCurrentRoadmap,
    showGoals,
    // actions
    setSelectedPath,
    setComparisonPaths,
    exitComparisonMode,
    setNewlyCreatedPathID,
    setShowGoals,
    refreshSelectedPathDataFromLevers,
  }), [
    roadmapExplorerResource,
    pathsList,
    selectedPath,
    pathResource,
    comparisonPaths,
    _chartResource,
    goalsOnCurrentRoadmap,
    showGoals,
    setSelectedPath,
    exitComparisonMode,
    refreshSelectedPathDataFromLevers,
  ]);

  return (
    <RoadmapExplorerPathsContext.Provider value={value}>
      {children}
    </RoadmapExplorerPathsContext.Provider>
  );
};

RoadmapExplorerPathsProvider.propTypes = {
  resource: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
};

export const useRoadmapPaths = () => {
  const ctx = useContext(RoadmapExplorerPathsContext);
  if (!ctx) throw new Error('useRoadmapPaths must be used within RoadmapExplorerPathsProvider');
  return ctx;
};
